// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Image, Modal, Platform, Pressable, ScrollView, StyleSheet, TextInput, View } from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import { useIsFocused } from '@react-navigation/native';
import { usePathname, useRouter } from 'expo-router';
import { SvgXml } from 'react-native-svg';
import { Text } from '@/components/Themed';
import GoffyButton from '@/components/GoffyButton';
import PlatformDatePicker from '@/components/PlatformDatePicker';
import { CoachMarkCallout } from '@/components/CoachMarkCallout';
import { useUser } from '@/app/user-context';
import { useI18n } from '@/i18n';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { createAvatarSvg } from '@/services/avatarSvg';
import { getSharedBffWebSocket } from '@/services/bffWebSocket';
import { profileImageActionsCoachMarkKey, useCoachMark } from '@/services/coachMarks';
import { formatLocalDate, localDateToDate } from '@/services/localDate';
import appColors from '@/theme/colors';

type PlayerProfile = {
  id: string;
  name?: string;
  displayName?: string | null;
  imageKey?: string | null;
  imageThumbKey?: string | null;
  gender?: string | null;
  handicap?: number | null;
  handicapDate?: string | null;
  Gender?: string | null;
  Handicap?: number | null;
  HandicapDate?: string | null;
};

type UserProfile = {
  id: string;
  forename?: string;
  email?: string | null;
  createdAt?: string;
  homeClubs?: HomeClubProfile[] | null;
};

type HomeClubProfile = {
  clubId: string;
  clubName: string;
  clubCity?: string | null;
  isPrimary?: boolean;
};

type ClubOption = {
  id: string;
  name: string;
  city?: string;
  country?: string;
};

type PendingUpload = {
  blob: Blob;
  contentType: string;
  fileName: string;
  fileSize: number;
};

export default function ProfileScreen() {
  const { user } = useUser();
  const { t } = useI18n();
  const isFocused = useIsFocused();
  const pathname = usePathname();
  const router = useRouter();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const socketRef = useRef(getSharedBffWebSocket(apiBaseUrl));
  const imageActionsAnchorRef = useRef<View>(null);
  const [imageCoachMarkDismissed, setImageCoachMarkDismissed] = useState(false);
  const {
    visible: showImageCoachMark,
    shownCount: imageCoachMarkShownCount,
    maxShows: imageCoachMarkMaxShows,
  } = useCoachMark(
    apiBaseUrl,
    profileImageActionsCoachMarkKey,
    !imageCoachMarkDismissed && isFocused
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [player, setPlayer] = useState<PlayerProfile | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [initialEmail, setInitialEmail] = useState('');
  const [homeClubs, setHomeClubs] = useState<HomeClubProfile[]>([]);
  const [initialHomeClubs, setInitialHomeClubs] = useState<HomeClubProfile[]>([]);
  const [clubOptions, setClubOptions] = useState<ClubOption[]>([]);
  const [loadingClubOptions, setLoadingClubOptions] = useState(false);
  const [clubPickerOpen, setClubPickerOpen] = useState(false);
  const [clubSearch, setClubSearch] = useState('');
  const [gender, setGender] = useState('');
  const [handicap, setHandicap] = useState('');
  const [handicapDate, setHandicapDate] = useState('');
  const [handicapDatePickerOpen, setHandicapDatePickerOpen] = useState(false);
  const [statusNote, setStatusNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [avatarPickerOpen, setAvatarPickerOpen] = useState(false);
  const [avatarChoices, setAvatarChoices] = useState<Array<{ id: string; seed: string }>>([]);
  const [avatarRemovedLocally, setAvatarRemovedLocally] = useState(false);
  const avatarRefreshLock = useRef(false);
  const pendingUploadRef = useRef<PendingUpload | null>(null);
  const imageRequestIdRef = useRef<string | null>(null);
  const updateRequestIdRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const resolvedImage = avatarRemovedLocally ? null : player?.imageThumbKey || player?.imageKey || null;
  const genderOptions = useMemo(
    () => [
      { value: 'male', label: t('profile.genderMale') },
      { value: 'female', label: t('profile.genderFemale') },
      { value: 'neutral', label: t('profile.genderNeutral') },
    ],
    [t]
  );
  const normalizedHomeClubs = useCallback(
    (items: HomeClubProfile[]) =>
      items
        .map((item, index) => ({
          clubId: item.clubId,
          clubName: item.clubName?.trim() ?? '',
          clubCity: item.clubCity?.trim() || null,
          isPrimary: item.isPrimary === true,
          _order: index,
        }))
        .sort((a, b) => {
          if (a.isPrimary === b.isPrimary) {
            if (a.clubId === b.clubId) {
              return a._order - b._order;
            }
            return a.clubId.localeCompare(b.clubId);
          }
          return a.isPrimary ? -1 : 1;
        })
        .map(({ _order, ...rest }) => rest),
    []
  );
  const filteredClubOptions = useMemo(() => {
    const query = clubSearch.trim().toLowerCase();
    const selectedIds = new Set(homeClubs.map((club) => club.clubId));
    const available = clubOptions.filter((club) => !selectedIds.has(club.id));
    if (!query) {
      return available;
    }
    return available.filter((club) => {
      const name = club.name.toLowerCase();
      const city = (club.city ?? '').toLowerCase();
      const country = (club.country ?? '').toLowerCase();
      return name.includes(query) || city.includes(query) || country.includes(query);
    });
  }, [clubOptions, clubSearch, homeClubs]);

  const loadPlayer = useCallback(async () => {
    if (!user?.token || !user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiBaseUrl}/api/player/by-user/${user.id}`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      if (!response.ok) {
        throw new Error('profile.load.failed');
      }
      const payload = (await response.json()) as PlayerProfile;
      setPlayer(payload);
      setDisplayName(payload.displayName ?? payload.name ?? '');
      try {
        const userResponse = await fetch(`${apiBaseUrl}/api/users/${user.id}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (userResponse.ok) {
          const userPayload = (await userResponse.json()) as UserProfile;
          const loadedEmail = (userPayload.email ?? '').trim();
          setEmail(loadedEmail);
          setInitialEmail(loadedEmail);
          const loadedHomeClubs = (userPayload.homeClubs ?? []).map((club) => ({
            clubId: club.clubId,
            clubName: club.clubName,
            clubCity: club.clubCity ?? null,
            isPrimary: club.isPrimary === true,
          }));
          const normalizedLoadedHomeClubs = normalizedHomeClubs(loadedHomeClubs);
          setHomeClubs(normalizedLoadedHomeClubs);
          setInitialHomeClubs(normalizedLoadedHomeClubs);
        } else {
          setEmail('');
          setInitialEmail('');
          setHomeClubs([]);
          setInitialHomeClubs([]);
        }
      } catch {
        setEmail('');
        setInitialEmail('');
        setHomeClubs([]);
        setInitialHomeClubs([]);
      }
      const nextGender = payload.gender ?? payload.Gender ?? '';
      const nextHandicap = payload.handicap ?? payload.Handicap;
      const nextHandicapDate = payload.handicapDate ?? payload.HandicapDate ?? '';
      setGender(nextGender);
      setHandicap(nextHandicap === null || nextHandicap === undefined ? '' : String(nextHandicap));
      setHandicapDate(nextHandicapDate ? String(nextHandicapDate).slice(0, 10) : '');
    } catch {
      setError(t('profile.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, normalizedHomeClubs, t, user?.id, user?.token]);

  useEffect(() => {
    loadPlayer();
  }, [loadPlayer]);

  const loadClubOptions = useCallback(async () => {
    if (!user?.token) return;
    setLoadingClubOptions(true);
    try {
      const response = await fetch(`${apiBaseUrl}/api/geodata/clubs`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      if (!response.ok) {
        throw new Error('profile.homeClubsLoadFailed');
      }

      const payload = (await response.json()) as ClubOption[];
      setClubOptions(payload ?? []);
    } catch {
      setError(t('profile.homeClubsLoadFailed'));
    } finally {
      setLoadingClubOptions(false);
    }
  }, [apiBaseUrl, t, user?.token]);

  useEffect(() => {
    void loadClubOptions();
  }, [loadClubOptions]);

  useEffect(() => {
    const socket = socketRef.current;
    socket.setAuthToken(user?.token ?? null);
    const unsubscribe = socket.onMessage((message) => {
      const type = (message as { type?: string }).type;
      if (type === 'player.image.presign.ack') {
        const payload = message as { requestId?: string; uploadUrl?: string; publicUrl?: string };
        if (imageRequestIdRef.current && payload.requestId !== imageRequestIdRef.current) {
          return;
        }
        const pending = pendingUploadRef.current;
        if (!pending || !payload.uploadUrl || !payload.publicUrl) {
          imageRequestIdRef.current = null;
          pendingUploadRef.current = null;
          setUploading(false);
          setError(t('profile.uploadFailed'));
          return;
        }
        (async () => {
          try {
            const uploadResp = await fetch(payload.uploadUrl!, {
              method: 'PUT',
              headers: { 'Content-Type': pending.contentType },
              body: pending.blob,
            });
            if (!uploadResp.ok) {
              throw new Error(`Upload failed ${uploadResp.status}`);
            }
            const updateRequestId = generateId();
            updateRequestIdRef.current = updateRequestId;
            socketRef.current.send({
              type: 'player.profile.update',
              playerId: player?.id,
              requestId: updateRequestId,
              imageKey: payload.publicUrl,
              removeAvatar: false,
            });
            setAvatarRemovedLocally(false);
            setPlayer((prev) => (prev ? { ...prev, imageKey: payload.publicUrl, imageThumbKey: payload.publicUrl } : prev));
            setStatusNote(t('profile.uploadSuccess'));
          } catch {
            setError(t('profile.uploadFailed'));
          } finally {
            setUploading(false);
            imageRequestIdRef.current = null;
            pendingUploadRef.current = null;
          }
        })();
        return;
      }
      if (type === 'player.image.presign.error') {
        const payload = message as { requestId?: string; message?: string };
        if (imageRequestIdRef.current && payload.requestId !== imageRequestIdRef.current) {
          return;
        }
        imageRequestIdRef.current = null;
        pendingUploadRef.current = null;
        setUploading(false);
        setError(payload.message ?? t('profile.uploadFailed'));
        return;
      }
      if (type === 'player.update.ack') {
        const payload = message as { requestId?: string };
        if (updateRequestIdRef.current && payload.requestId !== updateRequestIdRef.current) {
          return;
        }
        updateRequestIdRef.current = null;
        setInitialEmail(email.trim());
        setInitialHomeClubs(normalizedHomeClubs(homeClubs));
        setSaving(false);
        setStatusNote(t('profile.saveSuccess'));
        return;
      }
      if (type === 'player.update.error') {
        const payload = message as { requestId?: string; message?: string };
        if (updateRequestIdRef.current && payload.requestId !== updateRequestIdRef.current) {
          return;
        }
        updateRequestIdRef.current = null;
        setAvatarRemovedLocally(false);
        setSaving(false);
        setError(payload.message ?? t('profile.saveFailed'));
        return;
      }
      if (type === 'players_snapshot') {
        const payload = message as { players?: PlayerProfile[]; Players?: PlayerProfile[] };
        const list = payload.players ?? payload.Players ?? [];
        const updated = list.find((p) => p.id === player?.id);
        if (updated) {
          setPlayer((prev) =>
            avatarRemovedLocally
              ? { ...(prev ?? updated), ...updated, imageKey: null, imageThumbKey: null }
              : { ...(prev ?? updated), ...updated }
          );
        }
      }
    });
    return () => {
      unsubscribe();
    };
  }, [avatarRemovedLocally, email, homeClubs, normalizedHomeClubs, player?.id, t, user?.token]);

  const sendProfileUpdate = useCallback(
    (
      nextName: string,
      nextEmail: string,
      nextGender: string,
      nextHandicap: number | null,
      nextHandicapDate: string | null,
      nextHomeClubs: HomeClubProfile[]
    ) => {
      if (!player?.id) return;
      setSaving(true);
      setError(null);
      setStatusNote(t('profile.saving'));
      const requestId = generateId();
      updateRequestIdRef.current = requestId;
      socketRef.current.setAuthToken(user?.token ?? null);
      socketRef.current.send({
        type: 'player.profile.update',
        playerId: player.id,
        requestId,
        displayName: nextName.trim(),
        email: nextEmail,
        gender: nextGender,
        handicap: nextHandicap,
        handicapDate: nextHandicapDate,
        homeClubs: nextHomeClubs.map((club) => ({
          clubId: club.clubId,
          clubName: club.clubName,
          clubCity: club.clubCity ?? null,
          isPrimary: club.isPrimary === true,
        })),
      });
    },
    [player?.id, t, user?.token]
  );

  const handleSaveProfile = useCallback(() => {
    const trimmed = displayName.trim();
    const trimmedEmail = email.trim();
    if (!player?.id) return;
    const normalizedGender = gender.trim();
    const handicapText = handicap.trim();
    const handicapDateText = handicapDate.trim();

    if (handicapText && !handicapDateText) {
      setError(t('profile.handicapDateRequired'));
      return;
    }
    if (handicapDateText && !handicapText) {
      setError(t('profile.handicapRequired'));
      return;
    }

    let parsedHandicap: number | null = null;
    if (handicapText) {
      parsedHandicap = Number.parseFloat(handicapText);
      if (!Number.isFinite(parsedHandicap)) {
        setError(t('profile.invalidHandicap'));
        return;
      }
    }

    let normalizedHandicapDate: string | null = null;
    if (handicapDateText) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(handicapDateText)) {
        setError(t('profile.invalidHandicapDate'));
        return;
      }
      normalizedHandicapDate = handicapDateText;
    }

    const currentName = player.displayName ?? player.name ?? '';
    const currentGender = (player.gender ?? player.Gender ?? '').trim();
    const currentHandicap = player.handicap ?? player.Handicap ?? null;
    const currentHandicapDateRaw = player.handicapDate ?? player.HandicapDate ?? null;
    const currentHandicapDate = currentHandicapDateRaw ? String(currentHandicapDateRaw).slice(0, 10) : null;
    const currentEmail = initialEmail.trim();
    const sameName = trimmed === currentName;
    const sameEmail = trimmedEmail === currentEmail;
    const sameGender = normalizedGender === currentGender;
    const sameHandicap = (currentHandicap === null ? null : Number(currentHandicap)) === parsedHandicap;
    const sameDate = currentHandicapDate === normalizedHandicapDate;
    const nextHomeClubs = normalizedHomeClubs(homeClubs);
    const sameHomeClubs = JSON.stringify(nextHomeClubs) === JSON.stringify(normalizedHomeClubs(initialHomeClubs));
    if (sameName && sameEmail && sameGender && sameHandicap && sameDate && sameHomeClubs) {
        return;
    }

    sendProfileUpdate(
      trimmed,
      trimmedEmail,
      normalizedGender,
      parsedHandicap,
      normalizedHandicapDate,
      nextHomeClubs
    );
  }, [displayName, email, gender, handicap, handicapDate, homeClubs, initialEmail, initialHomeClubs, normalizedHomeClubs, player, sendProfileUpdate, t]);

  const parseDateInput = (value: string) => localDateToDate(value);
  const formatDateInput = (value: Date) => formatLocalDate(value);

  const handleRemoveAvatar = useCallback(() => {
    if (!player?.id || (!player.imageKey && !player.imageThumbKey)) return;
    setSaving(true);
    setError(null);
    setStatusNote(t('profile.saving'));
    const requestId = generateId();
    updateRequestIdRef.current = requestId;
    socketRef.current.setAuthToken(user?.token ?? null);
    const sent = socketRef.current.send({
      type: 'player.profile.update',
      playerId: player.id,
      requestId,
      imageKey: null,
      removeAvatar: true,
    });
    if (!sent) {
      updateRequestIdRef.current = null;
      setSaving(false);
      setStatusNote(null);
      setError(t('profile.saveFailed'));
      return;
    }
    setAvatarRemovedLocally(true);
    setPlayer((prev) => (prev ? { ...prev, imageKey: null, imageThumbKey: null } : prev));
  }, [player?.id, player?.imageKey, player?.imageThumbKey, t, user?.token]);

  const buildAvatarSvg = useCallback((seed: string) => createAvatarSvg(seed), []);

  const generateAvatarChoices = useCallback(() => {
    setAvatarChoices(() =>
      Array.from({ length: 12 }, () => ({
        id: generateId(),
        seed: generateId(),
      }))
    );
  }, []);

  useEffect(() => {
    if (!avatarPickerOpen) return;
    if (avatarRefreshLock.current) return;
    avatarRefreshLock.current = true;
    if (avatarChoices.length === 0) {
      generateAvatarChoices();
    }
    setTimeout(() => {
      avatarRefreshLock.current = false;
    }, 0);
  }, [avatarPickerOpen, generateAvatarChoices, avatarChoices.length]);

  const openAvatarPicker = useCallback(() => {
    generateAvatarChoices();
    setAvatarPickerOpen(true);
  }, [generateAvatarChoices]);

  useEffect(() => {
    if (imageCoachMarkShownCount === 0) {
      setImageCoachMarkDismissed(false);
    }
  }, [imageCoachMarkShownCount]);

  useEffect(() => {
    if (!isFocused) {
      setImageCoachMarkDismissed(false);
    }
  }, [isFocused]);

  useEffect(() => {
    if (isFocused && imageCoachMarkShownCount < imageCoachMarkMaxShows) {
      setImageCoachMarkDismissed(false);
    }
  }, [isFocused]);

  useEffect(() => {
    if (pathname === '/profile' && imageCoachMarkShownCount < imageCoachMarkMaxShows) {
      setImageCoachMarkDismissed(false);
    }
  }, [imageCoachMarkMaxShows, imageCoachMarkShownCount, pathname]);

  const requestPresign = useCallback(
    (payload: PendingUpload) => {
      if (!player?.id) return;
      pendingUploadRef.current = payload;
      const requestId = generateId();
      imageRequestIdRef.current = requestId;
      setUploading(true);
      setStatusNote(t('profile.uploading'));
      setError(null);
      socketRef.current.setAuthToken(user?.token ?? null);
      socketRef.current.send({
        type: 'player.image.presign',
        playerId: player.id,
        requestId,
        fileName: payload.fileName,
        contentType: payload.contentType,
        fileSize: payload.fileSize,
      });
    },
    [player?.id, t, user?.token]
  );

  const selectAvatar = useCallback(
    (seed: string) => {
      if (!player?.id) return;
      const svg = buildAvatarSvg(seed);
      const blob = new Blob([svg], { type: 'image/svg+xml' });
      requestPresign({
        blob,
        contentType: 'image/svg+xml',
        fileName: `player-${player.id}-${seed}.svg`,
        fileSize: blob.size,
      });
      setAvatarPickerOpen(false);
    },
    [buildAvatarSvg, player?.id, requestPresign]
  );

  const handleWebSelect = useCallback(
    (event: any) => {
      const file = event.target.files?.[0] as File | undefined;
      if (!file || !player?.id) return;
      requestPresign({
        blob: file,
        contentType: file.type || 'image/jpeg',
        fileName: file.name,
        fileSize: file.size,
      });
      event.target.value = '';
    },
    [player?.id, requestPresign]
  );

  const handleMobileSelect = useCallback(async () => {
    if (!player?.id) return;
    const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!permission.granted) {
      setError(t('profile.permissionDenied'));
      return;
    }
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      quality: 0.9,
    });
    if (result.canceled || result.assets.length === 0) return;
    const asset = result.assets[0];
    const response = await fetch(asset.uri);
    const blob = await response.blob();
    requestPresign({
      blob,
      contentType: blob.type || 'image/jpeg',
      fileName: asset.fileName ?? `player-${player.id}.jpg`,
      fileSize: blob.size,
    });
  }, [player?.id, requestPresign, t]);

  const uploadButtonPress = useCallback(() => {
    if (uploading) return;
    if (Platform.OS === 'web') {
      fileInputRef.current?.click();
    } else {
      void handleMobileSelect();
    }
  }, [handleMobileSelect, uploading]);

  const goBack = useCallback(() => {
    if (typeof router.canGoBack === 'function' && router.canGoBack()) {
      router.back();
      return;
    }
    router.replace('/tournaments');
  }, [router]);

  const removeHomeClub = useCallback((clubId: string) => {
    setHomeClubs((prev) => {
      const remaining = prev.filter((club) => club.clubId !== clubId);
      if (remaining.length > 0 && !remaining.some((club) => club.isPrimary)) {
        return remaining.map((club, index) => ({ ...club, isPrimary: index === 0 }));
      }
      return remaining;
    });
  }, []);

  const setPrimaryHomeClub = useCallback((clubId: string) => {
    setHomeClubs((prev) => prev.map((club) => ({ ...club, isPrimary: club.clubId === clubId })));
  }, []);

  const addHomeClub = useCallback((club: ClubOption) => {
    setHomeClubs((prev) => {
      if (prev.some((existing) => existing.clubId === club.id)) {
        return prev;
      }
      const isPrimary = prev.length === 0;
      return [
        ...prev,
        {
          clubId: club.id,
          clubName: club.name,
          clubCity: club.city ?? null,
          isPrimary,
        },
      ];
    });
    setClubSearch('');
    setClubPickerOpen(false);
  }, []);

  return (
    <ScrollView style={styles.screen} contentContainerStyle={styles.container} keyboardShouldPersistTaps="handled">
      <Pressable style={styles.backButton} onPress={goBack}>
        <Text style={styles.backButtonText}>{t('menu.back')}</Text>
      </Pressable>
      <View style={styles.header}>
        <View style={styles.avatarWrap}>
          {resolvedImage ? (
            <Image source={{ uri: resolvedImage }} style={styles.avatar} resizeMode="cover" />
          ) : (
            <View style={styles.avatarPlaceholder} />
          )}
        </View>
        <View style={styles.headerContent}>
          <Text style={styles.title}>{t('profile.title')}</Text>
          {loading ? (
            <View style={styles.loadingRow}>
              <ActivityIndicator size="small" color="#A1D493" />
              <Text style={styles.meta}>{t('profile.loading')}</Text>
            </View>
          ) : null}
          {statusNote ? <Text style={styles.meta}>{statusNote}</Text> : null}
          {error ? <Text style={styles.error}>{error}</Text> : null}
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.label}>{t('profile.nameLabel')}</Text>
        <TextInput
          value={displayName}
          onChangeText={setDisplayName}
          editable={!loading && !saving}
          placeholder={t('profile.namePlaceholder')}
          placeholderTextColor="#A1D493"
          style={styles.input}
        />
        <Text style={styles.label}>{t('profile.emailLabel')}</Text>
        <TextInput
          value={email}
          onChangeText={setEmail}
          editable={!loading && !saving}
          placeholder={t('profile.emailPlaceholder')}
          placeholderTextColor="#A1D493"
          style={styles.input}
          keyboardType="email-address"
          autoCapitalize="none"
        />
        <Text style={styles.label}>{t('profile.genderLabel')}</Text>
        <View style={styles.genderOptionsRow}>
          {genderOptions.map((option) => {
            const selected = gender === option.value;
            return (
              <Pressable
                key={option.value}
                style={[styles.genderOptionButton, selected && styles.genderOptionButtonSelected]}
                onPress={() => setGender(option.value)}
                disabled={loading || saving}
              >
                <Text style={[styles.genderOptionText, selected && styles.genderOptionTextSelected]}>
                  {option.label}
                </Text>
              </Pressable>
            );
          })}
        </View>
        <View style={styles.inlineRow}>
          <View style={styles.inlineInputGroup}>
            <Text style={styles.label}>{t('profile.handicapLabel')}</Text>
            <TextInput
              value={handicap}
              onChangeText={setHandicap}
              editable={!loading && !saving}
              placeholder={t('profile.handicapPlaceholder')}
              placeholderTextColor="#A1D493"
              style={styles.input}
              keyboardType="numeric"
            />
          </View>
          <View style={styles.inlineInputGroup}>
            <Text style={styles.label}>{t('profile.handicapDateLabel')}</Text>
            <Pressable
              style={styles.input}
              onPress={() => setHandicapDatePickerOpen(true)}
              disabled={loading || saving}
            >
              <Text style={handicapDate ? styles.inputText : styles.inputPlaceholderText}>
                {handicapDate || t('profile.handicapDatePlaceholder')}
              </Text>
            </Pressable>
          </View>
        </View>
        <Text style={styles.label}>{t('profile.homeClubsLabel')}</Text>
        {homeClubs.length === 0 ? (
          <Text style={styles.meta}>{t('profile.homeClubsEmpty')}</Text>
        ) : (
          <View style={styles.homeClubList}>
            {homeClubs.map((club) => (
              <View key={club.clubId} style={styles.homeClubItem}>
                <View style={styles.homeClubTextWrap}>
                  <Text style={styles.homeClubName}>{club.clubName}</Text>
                  {club.clubCity ? <Text style={styles.homeClubMeta}>{club.clubCity}</Text> : null}
                </View>
                <View style={styles.homeClubActions}>
                  <Pressable
                    style={[styles.homeClubActionButton, club.isPrimary && styles.homeClubActionButtonPrimary]}
                    onPress={() => setPrimaryHomeClub(club.clubId)}
                    disabled={loading || saving}
                  >
                    <Text style={[styles.homeClubActionButtonText, club.isPrimary && styles.homeClubActionButtonTextPrimary]}>
                      {club.isPrimary ? t('profile.homeClubsPrimary') : t('profile.homeClubsSetPrimary')}
                    </Text>
                  </Pressable>
                  <Pressable
                    style={styles.homeClubRemoveButton}
                    onPress={() => removeHomeClub(club.clubId)}
                    disabled={loading || saving}
                  >
                    <Text style={styles.homeClubRemoveText}>{t('profile.homeClubsRemove')}</Text>
                  </Pressable>
                </View>
              </View>
            ))}
          </View>
        )}
        <Pressable
          style={[styles.addHomeClubButton, (loading || saving) && styles.primaryButtonDisabled]}
          onPress={() => {
            setClubSearch('');
            setClubPickerOpen(true);
          }}
          disabled={loading || saving}
        >
          <Text style={styles.addHomeClubButtonText}>{t('profile.homeClubsAdd')}</Text>
        </Pressable>
        <GoffyButton
          label={t('profile.save')}
          onPress={handleSaveProfile}
          disabled={saving}
          tone="accent"
          oldButtonStyle={styles.primaryButton}
          oldTextStyle={styles.primaryButtonText}
          oldDisabledStyle={styles.primaryButtonDisabled}
          style={styles.primaryButtonImageMode}
          textStyle={styles.primaryButtonImageText}
        />
      </View>

      <View style={styles.section}>
        <Text style={styles.label}>{t('profile.imageLabel')}</Text>
        <View style={styles.buttonRow} ref={imageActionsAnchorRef}>
          <GoffyButton
            label={t('profile.uploadImage')}
            onPress={uploadButtonPress}
            disabled={uploading}
            tone="dark"
            oldButtonStyle={styles.secondaryButton}
            oldTextStyle={styles.secondaryButtonText}
            oldDisabledStyle={styles.primaryButtonDisabled}
            style={styles.secondaryButtonImageMode}
            textStyle={styles.secondaryButtonImageText}
          />
          <GoffyButton
            label={t('profile.selectAvatar')}
            onPress={openAvatarPicker}
            tone="dark"
            oldButtonStyle={styles.secondaryButton}
            oldTextStyle={styles.secondaryButtonText}
            style={styles.secondaryButtonImageMode}
            textStyle={styles.secondaryButtonImageText}
          />
          <GoffyButton
            label={t('profile.removeAvatar')}
            onPress={handleRemoveAvatar}
            disabled={saving || uploading || !resolvedImage}
            tone="dark"
            oldButtonStyle={styles.secondaryButton}
            oldTextStyle={styles.secondaryButtonText}
            oldDisabledStyle={styles.primaryButtonDisabled}
            style={styles.secondaryButtonImageMode}
            textStyle={styles.secondaryButtonImageText}
          />
        </View>
      </View>

      {Platform.OS === 'web' ? (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleWebSelect}
          style={{ display: 'none' }}
        />
      ) : null}
      <Modal transparent visible={clubPickerOpen} animationType="fade" onRequestClose={() => setClubPickerOpen(false)}>
        <Pressable style={styles.modalBackdrop} onPress={() => setClubPickerOpen(false)}>
          <View style={styles.avatarModalCard}>
            <Text style={styles.avatarModalTitle}>{t('profile.homeClubsPickerTitle')}</Text>
            <TextInput
              value={clubSearch}
              onChangeText={setClubSearch}
              editable={!loadingClubOptions}
              placeholder={t('profile.homeClubsSearchPlaceholder')}
              placeholderTextColor={appColors.border.default}
              style={styles.input}
            />
            <ScrollView style={styles.homeClubPickerList} contentContainerStyle={styles.homeClubPickerListContent}>
              {loadingClubOptions ? <Text style={styles.avatarLoading}>{t('profile.homeClubsLoading')}</Text> : null}
              {!loadingClubOptions && filteredClubOptions.length === 0 ? (
                <Text style={styles.avatarLoading}>{t('profile.homeClubsNoResults')}</Text>
              ) : null}
              {!loadingClubOptions
                ? filteredClubOptions.map((club) => (
                    <Pressable key={club.id} style={styles.clubOptionRow} onPress={() => addHomeClub(club)}>
                      <Text style={styles.clubOptionName}>{club.name}</Text>
                      <Text style={styles.clubOptionMeta}>
                        {[club.city, club.country].filter(Boolean).join(', ') || t('profile.homeClubsUnknownLocation')}
                      </Text>
                    </Pressable>
                  ))
                : null}
            </ScrollView>
            <View style={styles.avatarModalActions}>
              <Pressable
                style={[styles.moreButton, loadingClubOptions && styles.primaryButtonDisabled]}
                onPress={() => {
                  void loadClubOptions();
                }}
                disabled={loadingClubOptions}
              >
                <Text style={styles.moreButtonText}>{t('profile.homeClubsRefresh')}</Text>
              </Pressable>
              <Pressable style={styles.closeButton} onPress={() => setClubPickerOpen(false)}>
                <Text style={styles.closeButtonText}>{t('profile.close')}</Text>
              </Pressable>
            </View>
          </View>
        </Pressable>
      </Modal>
      <Modal transparent visible={avatarPickerOpen} animationType="fade" onRequestClose={() => setAvatarPickerOpen(false)}>
        <Pressable style={styles.modalBackdrop} onPress={() => setAvatarPickerOpen(false)}>
          <View style={styles.avatarModalCard}>
            <Text style={styles.avatarModalTitle}>{t('profile.avatarPickerTitle')}</Text>
            <View style={styles.avatarGrid}>
              {avatarChoices.length === 0 ? (
                <Text style={styles.avatarLoading}>{t('profile.avatarLoading')}</Text>
              ) : (
                avatarChoices.map(({ id, seed }) => {
                  const svg = buildAvatarSvg(seed);
                  return (
                    <Pressable key={id} style={styles.avatarChoice} onPress={() => selectAvatar(seed)}>
                      <SvgXml xml={svg} width="100%" height="100%" />
                    </Pressable>
                  );
                })
              )}
            </View>
            <View style={styles.avatarModalActions}>
              <Pressable style={styles.moreButton} onPress={generateAvatarChoices}>
                <Text style={styles.moreButtonText}>{t('profile.avatarMore')}</Text>
              </Pressable>
              <Pressable style={styles.closeButton} onPress={() => setAvatarPickerOpen(false)}>
                <Text style={styles.closeButtonText}>{t('profile.close')}</Text>
              </Pressable>
            </View>
          </View>
        </Pressable>
      </Modal>
      <PlatformDatePicker
        open={handicapDatePickerOpen}
        date={parseDateInput(handicapDate) ?? new Date()}
        onConfirm={(date) => {
          setHandicapDate(formatDateInput(date));
          setHandicapDatePickerOpen(false);
        }}
        onCancel={() => setHandicapDatePickerOpen(false)}
      />
      <CoachMarkCallout
        visible={showImageCoachMark}
        targetRef={imageActionsAnchorRef}
        title={t('coachMarks.profileImageActionsTitle')}
        description={t('coachMarks.profileImageActionsBody')}
        actionLabel={t('coachMarks.gotIt')}
        onDismiss={() => setImageCoachMarkDismissed(true)}
      />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: '#07200D',
  },
  container: {
    flexGrow: 1,
    padding: 16,
    gap: 16,
  },
  header: {
    flexDirection: 'row',
    gap: 16,
    alignItems: 'center',
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#0C2510',
  },
  backButton: {
    alignSelf: 'flex-start',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#A1D493',
    backgroundColor: '#C8C8B0',
    paddingHorizontal: 10,
    height: 34,
    justifyContent: 'center',
  },
  backButtonText: {
    color: appColors.text.primary,
    fontWeight: '700',
    fontSize: 13,
  },
  avatarWrap: {
    width: 88,
    height: 88,
    borderRadius: 16,
    overflow: 'hidden',
    backgroundColor: '#07200D',
    borderWidth: 1,
    borderColor: '#1A3A20',
  },
  avatar: {
    width: '100%',
    height: '100%',
  },
  avatarPlaceholder: {
    flex: 1,
    backgroundColor: '#C8C8B0',
  },
  headerContent: {
    flex: 1,
    gap: 6,
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
    color: appColors.text.primary,
  },
  meta: {
    color: '#A1D493',
    fontSize: 12,
  },
  error: {
    color: '#fca5a5',
    fontSize: 12,
  },
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  section: {
    gap: 10,
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#07200D',
  },
  inlineRow: {
    flexDirection: 'row',
    gap: 10,
  },
  inlineInputGroup: {
    flex: 1,
    gap: 6,
  },
  genderOptionsRow: {
    flexDirection: 'row',
    gap: 10,
    flexWrap: 'wrap',
  },
  genderOptionButton: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#07200D',
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  genderOptionButtonSelected: {
    borderColor: '#A1D493',
    backgroundColor: '#1A3A20',
  },
  genderOptionText: {
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '700',
  },
  genderOptionTextSelected: {
    color: appColors.text.primary,
  },
  label: {
    color: appColors.text.primary,
    fontSize: 13,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  input: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#1A3A20',
    paddingHorizontal: 12,
    paddingVertical: 10,
    color: appColors.text.primary,
    fontSize: 14,
    backgroundColor: '#07200D',
  },
  readonlyInput: {
    opacity: 0.8,
  },
  inputText: {
    color: appColors.text.primary,
    fontSize: 14,
  },
  inputPlaceholderText: {
    color: '#A1D493',
    fontSize: 14,
  },
  primaryButton: {
    alignSelf: 'flex-start',
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 10,
    backgroundColor: '#A1D493',
  },
  primaryButtonDisabled: {
    opacity: 0.6,
  },
  primaryButtonImageMode: {
    width: 190,
    height: 56,
  },
  primaryButtonImageText: {
    fontSize: 16,
  },
  primaryButtonText: {
    color: appColors.text.primary,
    fontWeight: '700',
  },
  buttonRow: {
    flexDirection: 'row',
    gap: 10,
    flexWrap: 'wrap',
  },
  homeClubList: {
    gap: 8,
  },
  homeClubItem: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#1e4d22',
    backgroundColor: '#1b351a',
    padding: 10,
    gap: 8,
  },
  homeClubTextWrap: {
    gap: 2,
  },
  homeClubName: {
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '700',
  },
  homeClubMeta: {
    color: '#C8C8B0',
    fontSize: 12,
  },
  homeClubActions: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  homeClubActionButton: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: appColors.border.default,
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: '#07200D',
  },
  homeClubActionButtonPrimary: {
    backgroundColor: appColors.border.default,
  },
  homeClubActionButtonText: {
    color: '#C8C8B0',
    fontWeight: '700',
    fontSize: 12,
  },
  homeClubActionButtonTextPrimary: {
    color: appColors.text.primary,
  },
  homeClubRemoveButton: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#7f1d1d',
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: '#2b0b0b',
  },
  homeClubRemoveText: {
    color: '#fecaca',
    fontWeight: '700',
    fontSize: 12,
  },
  addHomeClubButton: {
    alignSelf: 'flex-start',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#1e4d22',
    backgroundColor: '#1b351a',
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  addHomeClubButtonText: {
    color: appColors.text.primary,
    fontWeight: '700',
    fontSize: 14,
  },
  secondaryButton: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#07200D',
  },
  secondaryButtonText: {
    color: appColors.text.primary,
    fontWeight: '600',
  },
  secondaryButtonImageMode: {
    width: 190,
    height: 56,
  },
  secondaryButtonImageText: {
    fontSize: 16,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(17,44,27,0.18)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  avatarModalCard: {
    width: '100%',
    maxWidth: 520,
    backgroundColor: '#07200D',
    borderRadius: 16,
    padding: 16,
    borderWidth: 1,
    borderColor: '#1A3A20',
  },
  avatarModalTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: appColors.text.primary,
    marginBottom: 12,
  },
  avatarGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  homeClubPickerList: {
    maxHeight: 320,
    marginTop: 10,
  },
  homeClubPickerListContent: {
    gap: 8,
    paddingBottom: 8,
  },
  clubOptionRow: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#1e4d22',
    backgroundColor: '#1b351a',
    paddingHorizontal: 10,
    paddingVertical: 10,
    gap: 2,
  },
  clubOptionName: {
    color: appColors.text.primary,
    fontWeight: '700',
    fontSize: 14,
  },
  clubOptionMeta: {
    color: '#C8C8B0',
    fontSize: 12,
  },
  avatarLoading: {
    color: appColors.text.primary,
    fontSize: 12,
  },
  avatarTabs: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 12,
  },
  avatarTab: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#07200D',
  },
  avatarTabActive: {
    backgroundColor: '#A1D493',
    borderColor: '#A1D493',
  },
  avatarTabText: {
    color: appColors.text.primary,
    fontWeight: '600',
    fontSize: 12,
  },
  avatarChoice: {
    width: 64,
    height: 64,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#1A3A20',
    overflow: 'hidden',
    backgroundColor: '#0C2510',
  },
  avatarChoiceImage: {
    width: '100%',
    height: '100%',
  },
  avatarChoiceSvg: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarModalActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 16,
  },
  moreButton: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#07200D',
  },
  moreButtonText: {
    color: appColors.text.primary,
    fontWeight: '600',
  },
  closeButton: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 10,
    backgroundColor: '#A1D493',
  },
  closeButtonText: {
    color: appColors.text.primary,
    fontWeight: '700',
  },
});

function generateId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return (crypto as any).randomUUID();
  }
  return `req_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}
